import React, { useEffect, useRef, useState } from 'react';
import { Form } from 'react-bootstrap';
import '../../styles/search.css';
import { createPortal } from 'react-dom';
import { SuggestionForBusinessReport } from '../../types/search/SuggestionForBusinessReport';

interface ProjectSearchInputProps {
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  suggestions: SuggestionForBusinessReport[];
  onSelect: (item: SuggestionForBusinessReport) => void; // クリック/エンター押下で候補を選択
  onEnter: (currentValue: string) => void; // エンター押下で候補を探す
  placeholder: string;
}

const ProjectSearchInput = ({
  value,
  onChange,
  suggestions = [],
  onSelect,
  onEnter,
  placeholder = '検索',
}: ProjectSearchInputProps) => {
  const [open, setOpen] = useState(false); // 検索候補の表示/非表示
  const [activeIndex, setActiveIndex] = useState(-1); // 検索候補のハイライト
  const containerRef = useRef<HTMLDivElement>(null); // 入力ラッパ
  const popupRef = useRef<HTMLUListElement>(null); // ポータル先UL

  // input外クリックで候補を閉じる（※ポータル側も内側として扱う）
  useEffect(() => {
    const onDocMouseDown = (e: MouseEvent) => {
      const target = e.target as Node;
      const insideInput = containerRef.current?.contains(target) ?? false;
      const insidePopup = popupRef.current?.contains(target) ?? false;
      if (!insideInput && !insidePopup) {
        setOpen(false);
        setActiveIndex(-1);
      }
    };
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, []);

  // キーボードでの制御
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (activeIndex >= 0 && suggestions.length > 0) {
        // 検索候補の選択
        onSelect(suggestions[activeIndex]);
        setOpen(false);
        setActiveIndex(-1);
      } else {
        // 検索
        setOpen(true);
        onEnter(value);
      }
      return;
    }

    if (suggestions.length === 0) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setOpen(true);
      // 最後の次は先頭へ
      setActiveIndex((prev) => (prev + 1) % suggestions.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setOpen(true);
      // 先頭の上は最後へ
      setActiveIndex((prev) =>
        prev === -1
          ? suggestions.length - 1
          : (prev - 1 + suggestions.length) % suggestions.length
      );
    } else if (e.key === 'Escape') {
      setOpen(false);
      setActiveIndex(-1);
    }
  };

  return (
    <div className="base-search" ref={containerRef}>
      <Form.Control
        type="text"
        value={value}
        onChange={(e) => onChange(e as React.ChangeEvent<HTMLInputElement>)}
        onFocus={() => suggestions.length > 0 && setOpen(true)}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        autoComplete="off"
        className="text-center"
      />

      {/* 検索候補（クリックで選択） */}
      {open &&
        suggestions.length > 0 &&
        containerRef.current &&
        createPortal(
          <ul
            ref={popupRef}
            className="base-search__list list-group shadow-sm"
            role="listbox"
            style={{
              position: 'absolute',
              top:
                containerRef.current.getBoundingClientRect().bottom +
                window.scrollY,
              left:
                containerRef.current.getBoundingClientRect().left +
                window.scrollX,
              width: containerRef.current.getBoundingClientRect().width,
              zIndex: 1000,
            }}
            onMouseDown={(e) => {
              // ドキュメントの mousedown に到達させない（誤閉じ防止）
              e.stopPropagation();
            }}
          >
            {suggestions.map((item, index) => {
              const isActive = index === activeIndex;
              return (
                <li key={item.value} className="list-group-item p-0">
                  <button
                    id={`option-${item.value}`}
                    type="button"
                    role="option"
                    aria-selected={isActive}
                    className={`list-group-item list-group-item-action w-100 text-start py-2 ${
                      isActive ? 'active' : ''
                    }`}
                    onMouseDown={(e) => {
                      // input blur → 外側扱い → 閉じる、を防止
                      e.preventDefault();
                      e.stopPropagation();
                    }}
                    onMouseEnter={() => setActiveIndex(index)}
                    onClick={() => {
                      onSelect(item);
                      setOpen(false);
                      setActiveIndex(-1);
                    }}
                  >
                    {item.label}
                  </button>
                </li>
              );
            })}
          </ul>,
          document.body
        )}
    </div>
  );
};

export default ProjectSearchInput;
